import { transformToProcedurePayload } from 'clickNgo/helpers/api'
import { Nullable } from 'cng-common/models/utils'

import { CampaignCallOutcome } from '../../model/campaign'
import { campaignBaseApi } from '../campaignBaseApi'
import {
  buildCallOutcomeCreateParams,
  buildCallOutcomeUpdateParams,
  mapCallOutcomeFromRawData,
} from '../mapping'
import {
  CampaignApiTags,
  CreateCallOutcomeParams,
  GetCampaignCallOutcomesParams,
  UpdateCallOutcomeParams,
} from '../models'
import {
  campaignProcedureUrl,
  SP_LUT_CAMPAIGN_CALL_OUTCOMES_CREATE,
  SP_LUT_CAMPAIGN_CALL_OUTCOMES_GET,
  SP_LUT_CAMPAIGN_CALL_OUTCOMES_UPDATE,
} from '../procedures'
import { mapFirstRow } from '../responseMappers'

const callOutcomesEndpoints = campaignBaseApi.injectEndpoints({
  endpoints: (build) => ({
    // Fetch campaign call outcomes
    // This endpoint retrieves the call outcomes for a specific campaign's calling list.
    // It provides details about the results of calls made during the campaign.
    getCampaignCallOutcomes: build.query<
      { items: CampaignCallOutcome[]; total: number },
      GetCampaignCallOutcomesParams
    >({
      transformResponse(response) {
        return {
          items: response.Data.map(mapCallOutcomeFromRawData),
          total: response.Total,
        }
      },

      // Provide a campaign-level tag only for queries without callingListId (used in reports).
      // Per-card queries (with callingListId) manage their state locally and don't need invalidation.
      providesTags: (_result, _error, params) =>
        !params.callingListId && params.campaignId
          ? [{ type: CampaignApiTags.CampaignCallOutcomes, id: params.campaignId }]
          : [],

      query({ page, pageSize, ...rest }) {
        return {
          url: campaignProcedureUrl(SP_LUT_CAMPAIGN_CALL_OUTCOMES_GET),
          method: 'POST',
          data: {
            ...transformToProcedurePayload(rest),
            page,
            pageSize,
          },
        }
      },
    }),

    // Create call outcome
    // This endpoint creates a new call outcome for a specific campaign.
    createCallOutcome: build.mutation<Nullable<CampaignCallOutcome>, CreateCallOutcomeParams>({
      query(params) {
        return {
          url: campaignProcedureUrl(SP_LUT_CAMPAIGN_CALL_OUTCOMES_CREATE),
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          data: buildCallOutcomeCreateParams(params),
        }
      },

      transformResponse: mapFirstRow(mapCallOutcomeFromRawData),

      invalidatesTags: (_result, _error, params) => [
        { type: CampaignApiTags.CampaignRatings, id: params.campaignId },
        { type: CampaignApiTags.CampaignCallOutcomes, id: params.campaignId },
      ],
    }),

    // Update call outcome
    // This endpoint updates the details of a specific call outcome.
    updateCallOutcome: build.mutation<Nullable<CampaignCallOutcome>, UpdateCallOutcomeParams>({
      query({ callOutcome }) {
        return {
          url: campaignProcedureUrl(SP_LUT_CAMPAIGN_CALL_OUTCOMES_UPDATE),
          method: 'POST',
          data: buildCallOutcomeUpdateParams({ callOutcome }),
        }
      },
      transformResponse: mapFirstRow(mapCallOutcomeFromRawData),

      invalidatesTags: (_result, _error, params) => [
        { type: CampaignApiTags.CampaignRatings, id: params.callOutcome.campaignId },
        { type: CampaignApiTags.CampaignCallOutcomes, id: params.callOutcome.campaignId },
      ],
    }),
  }),
})

export const {
  useGetCampaignCallOutcomesQuery,
  useCreateCallOutcomeMutation,
  useUpdateCallOutcomeMutation,
} = callOutcomesEndpoints
